import { useEffect, useRef, useState } from 'react'
import { Heart, LoaderCircle } from 'lucide-react'
import { motion } from 'motion/react'
import { supabase } from '@/integrations/supabase/client'
import { useLanguage } from '@/context/LanguageContext'

interface FavoriteBoardButtonProps {
  wishlistId: string
  initialCount?: number
  initiallyFavorited?: boolean
  compact?: boolean
}

interface BoardFavoriteResult {
  wishlist_id: string
  favorite_count: number
  favorited: boolean
}

export default function FavoriteBoardButton({
  wishlistId,
  initialCount = 0,
  initiallyFavorited = false,
  compact = false,
}: FavoriteBoardButtonProps) {
  const { language } = useLanguage()
  const italian = language === 'it'
  const [count, setCount] = useState(initialCount)
  const [favorited, setFavorited] = useState(initiallyFavorited)
  const [pending, setPending] = useState(false)
  const [signedIn, setSignedIn] = useState<boolean | null>(null)
  const inFlight = useRef(false)

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSignedIn(!!data.session)
    })
    return () => { cancelled = true }
  }, [])

  async function toggleFavorite() {
    if (inFlight.current || !signedIn) return
    inFlight.current = true
    setPending(true)
    const prevCount = count
    const prevFavorited = favorited
    // Optimistic update with rollback on error
    setFavorited(!favorited)
    setCount(favorited ? Math.max(0, count - 1) : count + 1)
    try {
      const { data, error } = await supabase.rpc('toggle_board_favorite', {
        _wishlist_id: wishlistId,
      })
      if (error) throw error
      const result = (data as unknown as BoardFavoriteResult[] | null)?.find(
        (row) => row.wishlist_id === wishlistId
      )
      if (result) {
        setFavorited(result.favorited)
        setCount(result.favorite_count)
      }
    } catch (cause) {
      console.error('Unable to toggle board favorite', cause)
      setFavorited(prevFavorited)
      setCount(prevCount)
    } finally {
      inFlight.current = false
      setPending(false)
    }
  }

  const sizeClasses = compact
    ? 'min-h-[32px] min-w-[32px] px-2 text-xs'
    : 'min-h-[44px] min-w-[44px] sm:px-4 px-3 text-sm'
  const iconSize = compact ? 'h-3.5 w-3.5' : 'h-4 w-4'
  const label = !signedIn
    ? (italian ? 'Accedi per salvare i preferiti' : 'Sign in to save favorites')
    : favorited
      ? (italian ? 'Rimuovi dai preferiti' : 'Remove from favorites')
      : (italian ? 'Aggiungi ai preferiti' : 'Add to favorites')

  return (
    <motion.button
      type="button"
      whileTap={signedIn && !pending ? { scale: 0.85 } : undefined}
      animate={favorited ? { scale: [1, 1.25, 1] } : { scale: 1 }}
      transition={{ duration: 0.25 }}
      title={label}
      aria-label={`${label} · ${count}`}
      aria-pressed={favorited}
      aria-busy={pending}
      disabled={pending}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void toggleFavorite()
      }}
      className={`inline-flex shrink-0 items-center justify-center gap-1 rounded-full border border-pink-500 bg-white/80 font-semibold text-pink-600 shadow-sm backdrop-blur-sm transition hover:bg-pink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70 ${sizeClasses}`}
    >
      {pending ? (
        <LoaderCircle aria-hidden="true" className={`${iconSize} animate-spin text-pink-600`} />
      ) : (
        <Heart aria-hidden="true" className={`${iconSize} text-pink-600 ${favorited ? 'fill-pink-500' : ''}`} />
      )}
      <span>{count}</span>
    </motion.button>
  )
}
